/* eslint-disable react/jsx-props-no-spreading */
/** @file Image display/uploader */
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Box } from '@mui/material';
import TechLifePhoto from './TechLifePhoto.svg';
import Placeholder from '../../images/transparent.svg';
import { Button } from '../../components/core';

/**
 * Main display of input. Shows existing image, temporary image, or placeholder
 * @param {Object} props Component properties
 * @param {string | File | undefined} props.img Existing image src, new image File, or undefined
 * @returns JSX.Element
 */
const InputBody = ({
  img,
  imageAlt = 'Brand Logo',
}: {
  img: string | File | undefined | null;
  imageAlt?: string;
}) => {
  /** Replace the broken image with a transparent one */
  const onError = ({ currentTarget }: { currentTarget: HTMLImageElement }) => {
    // eslint-disable-next-line no-param-reassign
    currentTarget.onerror = null; // prevents looping
    // eslint-disable-next-line no-param-reassign
    currentTarget.src = Placeholder;
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        width: '100%',
        '& > img': {
          maxHeight: '100%',
          maxWidth: '100%',
        },
      }}
    >
      {img ? (
        <img
          alt={imageAlt}
          src={typeof img === 'string' ? img : URL.createObjectURL(img)}
          onError={onError}
        />
      ) : (
        <img alt="Placeholder Brand Logo" src={TechLifePhoto} />
      )}
    </Box>
  );
};

type Props = {
  img: string | File | undefined | null;
  className?: string;
  onChange: (arg0: string | File) => void;
  onDelete: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  uploadText?: string;
  replaceText?: string;
  removeText?: string;
  imageAlt?: string;
};

/**
 * Image upload for a logo
 * @param {Object} props Component properties
 * @param {string} [props.className] Override root styling
 * @param {string | File | undefined} props.img Image to display
 * @param {Function} props.onChange Handler for when a new image is dropped/selected
 * @returns JSX.Element
 */
const LogoInput = ({
  img,
  onChange,
  onDelete,
  disabled,
  ariaLabel,
  uploadText = 'Upload a logo',
  replaceText = 'Replace Logo',
  removeText = 'Remove Logo',
  imageAlt,
}: Props) => {
  /** DropZone props */
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    onDrop: (acceptedFiles) => {
      onChange(acceptedFiles[0]);
    },
    disabled,
  });

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.300',
        width: { md: '312px', xs: '100%' },
        maxWidth: '312px',
        height: '261px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        borderRadius: '24px',
        '& > .dropzone': {
          alignItems: 'center',
          cursor: 'pointer',
          height: '180px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContents: 'center',
          outline: 'none',
          transition: 'border .24s ease-in-out',
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        },
      }}
    >
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} aria-label={ariaLabel || 'Logo'} />
        <InputBody img={img} imageAlt={imageAlt} />
      </div>
      {!disabled && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 2,
            '& > .dropzone': {
              width: '100%',
            },
          }}
        >
          <div {...getRootProps({ className: 'dropzone' })}>
            <Button
              text={img ? replaceText : uploadText}
              sx={{
                '& p': { fontSize: img ? '11px' : '14px' },
                height: img ? '25px' : '48px',
                width: '100%',
              }}
              data-testid="upload-logo"
            />
          </div>
          {img && (
            <Button
              text={removeText}
              theme="secondary"
              sx={{ '& p': { fontSize: '11px' }, height: '25px', width: '100%' }}
              data-testid="delete-img"
              onClick={onDelete}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default LogoInput;
